import { useEffect, useRef, useState, type HTMLAttributes, type PointerEvent as ReactPointerEvent, type ReactNode } from "react";
import { Grip } from "lucide-react";

type Geometry = { x: number; y: number; width: number; height: number };

const MIN_WIDTH = 380;
const MIN_HEIGHT = 480;

function defaultGeometry(): Geometry {
  const width = Math.min(680, window.innerWidth - 32);
  const height = Math.min(760, window.innerHeight - 96);
  return { x: window.innerWidth - width - 20, y: 80, width, height };
}

function constrain(geometry: Geometry): Geometry {
  const topMargin = window.innerHeight < 600 ? 8 : 72;
  const availableWidth = Math.max(280, window.innerWidth - 16);
  const availableHeight = Math.max(240, window.innerHeight - topMargin - 8);
  const minWidth = Math.min(MIN_WIDTH, availableWidth);
  const minHeight = Math.min(MIN_HEIGHT, availableHeight);
  const width = Math.max(minWidth, Math.min(geometry.width, availableWidth));
  const height = Math.max(minHeight, Math.min(geometry.height, availableHeight));
  return {
    width,
    height,
    x: Math.max(8, Math.min(geometry.x, window.innerWidth - width - 8)),
    y: Math.max(topMargin, Math.min(geometry.y, window.innerHeight - height - 8)),
  };
}

function readGeometry(key: string): Geometry {
  try {
    const saved = JSON.parse(localStorage.getItem(key) ?? "null") as Partial<Geometry> | null;
    if (saved && [saved.x, saved.y, saved.width, saved.height].every((value) => typeof value === "number" && Number.isFinite(value))) {
      return saved as Geometry;
    }
  } catch {
    // A broken preference should never prevent the Agent from opening.
  }
  return defaultGeometry();
}

export function AgentFloating({
  open,
  onClose,
  owner,
  children,
}: {
  open: boolean;
  onClose: () => void;
  owner: string;
  children: (dragHandleProps: HTMLAttributes<HTMLElement> & { "data-agent-drag-handle"?: boolean }) => ReactNode;
}) {
  const storageKey = `huopan-agent-window-v1:${owner}`;
  const [geometry, setGeometry] = useState<Geometry>(() => constrain(readGeometry(storageKey)));
  const [isMobile, setIsMobile] = useState(() => window.matchMedia("(max-width: 767px)").matches);
  const geometryRef = useRef(geometry);
  const preferredGeometryRef = useRef<Geometry>(readGeometry(storageKey));
  geometryRef.current = geometry;

  const commitGeometry = (next: Geometry) => {
    preferredGeometryRef.current = next;
    geometryRef.current = next;
    setGeometry(next);
    localStorage.setItem(storageKey, JSON.stringify(next));
  };

  useEffect(() => {
    const media = window.matchMedia("(max-width: 767px)");
    const onMediaChange = () => setIsMobile(media.matches);
    const onResize = () => {
      if (window.innerWidth < 1 || window.innerHeight < 1) return;
      setGeometry(constrain(preferredGeometryRef.current));
    };
    media.addEventListener("change", onMediaChange);
    window.addEventListener("resize", onResize);
    return () => {
      media.removeEventListener("change", onMediaChange);
      window.removeEventListener("resize", onResize);
    };
  }, []);

  const startDrag = (event: ReactPointerEvent<HTMLElement>) => {
    if (isMobile || event.button !== 0 || (event.target as HTMLElement).closest("button, input, textarea, select, a")) return;
    event.preventDefault();
    const pointerX = event.clientX;
    const pointerY = event.clientY;
    const start = geometryRef.current;
    const onMove = (moveEvent: PointerEvent) => {
      commitGeometry(constrain({ ...start, x: start.x + moveEvent.clientX - pointerX, y: start.y + moveEvent.clientY - pointerY }));
    };
    const onUp = () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp, { once: true });
  };

  const startResize = (event: ReactPointerEvent<HTMLButtonElement>) => {
    if (isMobile || event.button !== 0) return;
    event.preventDefault();
    event.stopPropagation();
    const pointerX = event.clientX;
    const pointerY = event.clientY;
    const start = geometryRef.current;
    const onMove = (moveEvent: PointerEvent) => {
      commitGeometry(constrain({
        ...start,
        width: start.width + moveEvent.clientX - pointerX,
        height: start.height + moveEvent.clientY - pointerY,
      }));
    };
    const onUp = () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp, { once: true });
  };

  const style = isMobile
    ? { inset: "8px", maxWidth: "calc(100vw - 16px)", maxHeight: "calc(100dvh - 16px)" }
    : { left: geometry.x, top: geometry.y, width: geometry.width, height: geometry.height, maxWidth: "calc(100vw - 16px)", maxHeight: "calc(100dvh - 16px)" };

  return (
    <section
      aria-label="交易 Agent 浮窗"
      data-agent-floating
      className={`fixed z-40 min-w-0 overflow-hidden rounded-lg border bg-card shadow-xl ${open ? "block" : "hidden"}`}
      style={style}
      onKeyDown={(event) => { if (event.key === "Escape") onClose(); }}
    >
      {children({ onPointerDown: startDrag, "data-agent-drag-handle": true })}
      {!isMobile && (
        <button
          type="button"
          aria-label="调整 Agent 浮窗大小"
          title="拖拽调整大小；方向键调整宽度，Shift + 方向键调整高度"
          data-agent-resize-handle
          className="absolute right-0 bottom-0 z-10 grid size-11 cursor-nwse-resize place-items-end rounded-tl-lg p-2 text-muted-foreground outline-none transition-colors hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring"
          onPointerDown={startResize}
          onKeyDown={(event) => {
            const horizontal = event.key === "ArrowRight" ? 16 : event.key === "ArrowLeft" ? -16 : 0;
            const vertical = event.key === "ArrowDown" ? 16 : event.key === "ArrowUp" ? -16 : 0;
            if (!horizontal && !vertical) return;
            event.preventDefault();
            const current = geometryRef.current;
            commitGeometry(constrain({
              ...current,
              width: current.width + (event.shiftKey ? 0 : horizontal || vertical),
              height: current.height + (event.shiftKey ? horizontal || vertical : 0),
            }));
          }}
        >
          <Grip className="size-4 rotate-90" />
        </button>
      )}
    </section>
  );
}
